import { observer } from "mobx-react";
import React from "react";
import {
  Paper,
  Stack,
  Text,
  Title,
  Group,
  Anchor,
  Box,
  CopyButton,
  ActionIcon,
  Tooltip,
  Badge,
} from "@mantine/core";
import { IconCopy, IconCheck, IconExternalLink } from "@tabler/icons-react";
import { getAmbossLink, formatPubkeyForDisplay } from "../helpers/types";

/**
 * Security: Sanitize alias to prevent XSS from malicious node operators
 * Removes HTML tags and limits length
 */
const sanitizeAlias = (alias: string | null | undefined): string | null => {
  if (!alias) return null;
  // Remove any HTML/script tags and limit to 50 chars
  return alias.replace(/<[^>]*>/g, "").substring(0, 50);
};

/**
 * Convert a short channel ID from hex or decimal string to the human-readable format.
 * Format: block_height x tx_index x output_index
 * The SCID is a 64-bit integer packed as: (block_height << 40) | (tx_index << 16) | output_index
 */
const parseShortChannelId = (
  scid: string
): { hex: string; integer: string; formatted: string } => {
  // Parse the input - could be hex (with or without 0x) or decimal
  let scidBigInt: bigint;
  try {
    if (scid.startsWith("0x") || /^[0-9a-fA-F]+$/.test(scid)) {
      // Hex format
      scidBigInt = BigInt("0x" + scid.replace(/^0x/, ""));
    } else {
      // Decimal format
      scidBigInt = BigInt(scid);
    }
  } catch {
    return { hex: scid, integer: scid, formatted: scid };
  }

  // Extract components using bit manipulation
  const blockHeight = scidBigInt >> 40n;
  const txIndex = (scidBigInt >> 16n) & 0xffffffn;
  const outputIndex = scidBigInt & 0xffffn;

  const hex = scidBigInt.toString(16).padStart(16, "0");
  const integer = scidBigInt.toString(10);
  const formatted = `${blockHeight}x${txIndex}x${outputIndex}`;

  return { hex, integer, formatted };
};

interface RouteHintHop {
  pubkey: string;
  short_channel_id: string;
  fee_base_msat: number;
  fee_proportional_millionths: number;
  cltv_expiry_delta: number;
}

interface RouteHintsDisplayProps {
  routeHints: RouteHintHop[][];
  pubkeyAliases?: Record<string, string | null>;
}

const RouteHintsDisplay: React.FC<RouteHintsDisplayProps> = observer(
  ({ routeHints, pubkeyAliases = {} }) => {
    if (routeHints.length === 0) return null;

    return (
      <Stack gap="sm">
        <Title order={5} c="white">
          Route Hints ({routeHints.length})
        </Title>
        <Text size="xs" c="dimmed">
          These are suggested routing paths to reach the payee
        </Text>

        {routeHints.map((hint, hintIndex) => (
          <Paper
            key={hintIndex}
            p="sm"
            radius="sm"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              border: "1px solid rgba(255, 255, 255, 0.08)",
            }}
          >
            <Stack gap="xs">
              <Badge size="sm" variant="light" color="gray">
                Route {hintIndex + 1}
              </Badge>
              {hint.map((hop, hopIndex) => (
                <HopDisplay
                  key={hopIndex}
                  hop={hop}
                  hopIndex={hopIndex}
                  alias={pubkeyAliases[hop.pubkey] ?? null}
                />
              ))}
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  }
);

interface HopDisplayProps {
  hop: RouteHintHop;
  hopIndex: number;
  alias?: string | null;
}

const HopDisplay: React.FC<HopDisplayProps> = ({ hop, hopIndex, alias }) => {
  const safeAlias = sanitizeAlias(alias);
  const channelId = parseShortChannelId(hop.short_channel_id);
  return (
    <Box
      p="xs"
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        borderRadius: "4px",
      }}
    >
      <Stack gap={4}>
        {/* Pubkey with Amboss link */}
        <Box>
          {safeAlias && (
            <>
              <Text size="xs" c="dimmed" mb={2}>
                Node Alias
              </Text>
              <Text size="sm" c="white" fw={600} mb={8}>
                {safeAlias}
              </Text>
            </>
          )}
          <Text size="xs" c="dimmed" mb={2}>
            Node Public Key
          </Text>
          <Group gap="xs" wrap="nowrap">
            <Anchor
              href={getAmbossLink(hop.pubkey)}
              target="_blank"
              rel="noopener noreferrer"
              size="xs"
              c="cyan"
              style={{ fontFamily: "monospace", wordBreak: "break-all", minWidth: 0 }}
            >
              {hop.pubkey}
              <IconExternalLink
                size={12}
                style={{ marginLeft: 4, verticalAlign: "middle" }}
              />
            </Anchor>
            <CopyButton value={hop.pubkey}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? "Copied!" : "Copy pubkey"} withArrow>
                  <ActionIcon
                    variant="subtle"
                    color={copied ? "green" : "gray"}
                    onClick={copy}
                    size="xs"
                  >
                    {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
        </Box>

        {/* Channel ID - all formats */}
        <Box>
          <Text size="xs" c="dimmed" mb={2}>
            Channel ID
          </Text>
          <Group gap="md">
            <Box>
              <Text size="xs" c="dimmed">
                Short
              </Text>
              <Text size="xs" c="white" style={{ fontFamily: "monospace" }}>
                {channelId.formatted}
              </Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">
                Integer
              </Text>
              <Text size="xs" c="white" style={{ fontFamily: "monospace" }}>
                {channelId.integer}
              </Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">
                Hex
              </Text>
              <Text size="xs" c="white" style={{ fontFamily: "monospace" }}>
                {channelId.hex}
              </Text>
            </Box>
          </Group>
        </Box>

        {/* Fee info */}
        <Group gap="lg">
          <Box>
            <Text size="xs" c="dimmed">
              Base Fee
            </Text>
            <Text size="xs" c="white">
              {hop.fee_base_msat} msat
            </Text>
          </Box>

          <Box>
            <Text size="xs" c="dimmed">
              Fee Rate
            </Text>
            <Text size="xs" c="white">
              {hop.fee_proportional_millionths} ppm
            </Text>
          </Box>

          <Box>
            <Text size="xs" c="dimmed">
              CLTV Delta
            </Text>
            <Text size="xs" c="white">
              {hop.cltv_expiry_delta} blocks
            </Text>
          </Box>
        </Group>
      </Stack>
    </Box>
  );
};

export default RouteHintsDisplay;
